import React, { useState } from 'react';
import { X,  FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import './UploadSection.css';

const UploadSection = ({ 
  title, 
  icon: Icon, 
  file, 
  setFile, 
  text, 
  setText, 
  placeholder, 
  accept,
  theme 
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  return (
    <motion.div 
      className="upload-section-modern"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="upload-header-modern">
        <div className="header-icon-wrapper">
          <Icon className="upload-header-icon-modern" />
        </div>
        <h3>{title}</h3>
      </div>

      <motion.div 
        className={`upload-zone-modern ${isDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
        onClick={() => !file && document.getElementById(`file-input-${title}`).click()}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        whileHover={!file ? { scale: 1.01 } : {}}
      >
        <input
          type="file"
          id={`file-input-${title}`}
          className="file-input-hidden"
          accept={accept}
          onChange={handleFileChange}
        />
        
        {!file ? (
          <div className="upload-prompt-modern">
            {/* New Cloud Document SVG with Animation */}
            <motion.div 
              className="upload-icon-wrapper"
              animate={{ 
                y: [0, -10, 0],
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <svg 
                className="upload-svg-icon" 
                xmlns="http://www.w3.org/2000/svg" 
                version="1.1" 
                width="112" 
                height="112" 
                viewBox="0 0 55 55"
              >
                <motion.path 
                  fill="#ff5f6e" 
                  d="M45.697 30.423c.617-2.038-.015-4.384-1.32-6.02-1.391-1.741-3.554-2.797-5.774-3-2.219-.202-4.606.41-6.519 1.552-2.694-5.5-10.565-6.94-16.148-1.994-2.348 2.08-3.999 4.58-4.72 7.633-2.373-.226-4.775 1.01-6.16 2.95-3.213 4.5-1.284 11.92 4.26 13.33 2.057.524 4.267.537 6.374.716 7.622.655 22.817 2.498 30.112-.809 3.719-1.685 6.947-5.915 5.22-10.133a5.59 5.59 0 0 0-2.282-2.637c-.558-.335-3.127-.694-3.145-1.077-.008-.174.051-.343.102-.51z"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
                <motion.path 
                  fill="#3a97d4" 
                  d="M49.925 30.973c2.38-6.049-1.462-13.064-8.03-13.845-5.593-.666-11.004 3.786-11.432 9.403-.479 6.29 5.21 11.57 11.43 10.726 3.555-.481 6.72-2.95 8.032-6.284z"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                />
                <motion.path 
                  d="M29.49 26.493c.247-3.238 2.112-6.289 4.99-8.16a.499.499 0 1 1 .545.838c-2.618 1.703-4.314 4.468-4.537 7.399-.453 5.948 4.931 10.997 10.864 10.192.654-.092.792.902.135.99-6.564.889-12.498-4.673-11.996-11.259z" 
                  fill="#000000"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 1.5, delay: 0.4 }}
                />
                <motion.path 
                  fill="#ffffff" 
                  d="M40.899 23.41c.538.025.952.451.925.951-.124 2.26-.186 4.554-.182 6.817 0 .501-.452.919-.976.908-.538 0-.974-.405-.975-.906-.004-2.294.058-4.62.184-6.911.028-.511.502-.888 1.024-.86z"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                />
                <motion.path 
                  fill="#ffffff" 
                  d="M41.29 22.494c1.022.704 2.041 1.952 2.884 2.532a.976.976 0 0 1-1.108 1.606c-.796-.549-1.52-1.354-2.219-1.99-.518.54-1.187 1.16-2.022 1.873a.976.976 0 0 1-1.267-1.482c1.13-.968 1.945-1.757 2.418-2.347a.976.976 0 0 1 1.315-.192z"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                />
              </svg>
            </motion.div>
            
            <div className="upload-text-content">
              <p className="upload-main-text">
                {isDragging ? 'Drop your file here' : 'Click to upload or drag & drop'}
              </p>
              <span className="upload-hint-text">PDF, DOCX, TXT • Max 10MB</span>
            </div>

            {/* Drag Overlay */}
            {isDragging && (
              <motion.div 
                className="drag-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="drag-icon-large">📄</div>
                <p>Drop to upload</p>
              </motion.div>
            )}
          </div>
        ) : (
          <motion.div 
            className="file-display-modern" 
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <div className="file-info">
              <FileText className="file-icon" size={24} />
              <div className="file-details">
                <span className="file-name-modern">{file.name}</span>
                <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
              </div>
            </div>
            <motion.button 
              className="remove-file-btn-modern" 
              onClick={handleRemoveFile}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={18} />
            </motion.button>
          </motion.div>
        )}
      </motion.div>

      <div className="divider-modern">
        <span>OR PASTE CONTENT</span>
      </div>

      <textarea
        className="paste-area-modern"
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={!!file}
      />
    </motion.div>
  );
};

export default UploadSection;
